import React from 'react';
import { Container, Typography, Box, Paper, Grid, Card, CardContent, Chip, Stack } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { Link as RouterLink } from 'react-router-dom';
import { Link as MuiLink } from '@mui/material';

const StateNexusGuide: React.FC = () => {
  const states = [
    { name: 'Alabama', threshold: '$250,000', transactions: 'N/A', notes: 'Sales threshold only' },
    { name: 'Arizona', threshold: '$100,000', transactions: 'N/A', notes: 'Sales threshold only' },
    { name: 'Arkansas', threshold: '$100,000', transactions: '200', notes: 'Either threshold triggers nexus' },
    { name: 'California', threshold: '$500,000', transactions: 'N/A', notes: 'Sales threshold only' },
    { name: 'Colorado', threshold: '$100,000', transactions: 'N/A', notes: 'Sales threshold only' },
    { name: 'Connecticut', threshold: '$100,000', transactions: '200', notes: 'Either threshold triggers nexus' },
    { name: 'Florida', threshold: '$100,000', transactions: 'N/A', notes: 'Sales threshold only' },
    { name: 'Georgia', threshold: '$100,000', transactions: '200', notes: 'Either threshold triggers nexus' },
    { name: 'Illinois', threshold: '$100,000', transactions: '200', notes: 'Either threshold triggers nexus' },
    { name: 'Indiana', threshold: '$100,000', transactions: '200', notes: 'Either threshold triggers nexus' },
    { name: 'Iowa', threshold: '$100,000', transactions: 'N/A', notes: 'Sales threshold only' },
    { name: 'Kansas', threshold: '$100,000', transactions: 'N/A', notes: 'Sales threshold only' },
    { name: 'Kentucky', threshold: '$100,000', transactions: '200', notes: 'Either threshold triggers nexus' },
    { name: 'Louisiana', threshold: '$100,000', transactions: '200', notes: 'Either threshold triggers nexus' },
    { name: 'Maine', threshold: '$100,000', transactions: 'N/A', notes: 'Sales threshold only' },
    { name: 'Maryland', threshold: '$100,000', transactions: '200', notes: 'Either threshold triggers nexus' },
    { name: 'Massachusetts', threshold: '$100,000', transactions: 'N/A', notes: 'Sales threshold only' },
    { name: 'Michigan', threshold: '$100,000', transactions: '200', notes: 'Either threshold triggers nexus' },
    { name: 'Minnesota', threshold: '$100,000', transactions: '200', notes: 'Either threshold triggers nexus' },
    { name: 'Mississippi', threshold: '$250,000', transactions: 'N/A', notes: 'Sales threshold only' },
    { name: 'Missouri', threshold: '$100,000', transactions: 'N/A', notes: 'Sales threshold only' },
    { name: 'Nebraska', threshold: '$100,000', transactions: '200', notes: 'Either threshold triggers nexus' },
    { name: 'Nevada', threshold: '$100,000', transactions: '200', notes: 'Either threshold triggers nexus' },
    { name: 'New Jersey', threshold: '$100,000', transactions: '200', notes: 'Either threshold triggers nexus' },
    { name: 'New Mexico', threshold: '$100,000', transactions: 'N/A', notes: 'Sales threshold only' },
    { name: 'New York', threshold: '$500,000', transactions: '100', notes: 'Either threshold triggers nexus' },
    { name: 'North Carolina', threshold: '$100,000', transactions: '200', notes: 'Either threshold triggers nexus' },
    { name: 'North Dakota', threshold: '$100,000', transactions: 'N/A', notes: 'Sales threshold only' },
    { name: 'Ohio', threshold: '$100,000', transactions: '200', notes: 'Either threshold triggers nexus' },
    { name: 'Oklahoma', threshold: '$100,000', transactions: 'N/A', notes: 'Sales threshold only' },
    { name: 'Pennsylvania', threshold: '$100,000', transactions: 'N/A', notes: 'Sales threshold only' },
    { name: 'Rhode Island', threshold: '$100,000', transactions: '200', notes: 'Either threshold triggers nexus' },
    { name: 'South Carolina', threshold: '$100,000', transactions: 'N/A', notes: 'Sales threshold only' },
    { name: 'South Dakota', threshold: '$100,000', transactions: '200', notes: 'Either threshold triggers nexus' },
    { name: 'Tennessee', threshold: '$100,000', transactions: 'N/A', notes: 'Sales threshold only' },
    { name: 'Texas', threshold: '$500,000', transactions: 'N/A', notes: 'Sales threshold only' },
    { name: 'Utah', threshold: '$100,000', transactions: '200', notes: 'Either threshold triggers nexus' },
    { name: 'Vermont', threshold: '$100,000', transactions: '200', notes: 'Either threshold triggers nexus' },
    { name: 'Virginia', threshold: '$100,000', transactions: '200', notes: 'Either threshold triggers nexus' },
    { name: 'Washington', threshold: '$100,000', transactions: 'N/A', notes: 'Sales threshold only' },
    { name: 'West Virginia', threshold: '$100,000', transactions: '200', notes: 'Either threshold triggers nexus' },
    { name: 'Wisconsin', threshold: '$100,000', transactions: 'N/A', notes: 'Sales threshold only' },
    { name: 'Wyoming', threshold: '$100,000', transactions: '200', notes: 'Either threshold triggers nexus' },
  ];

  return (
    <>
      <Helmet>
        <title>2025 Economic Nexus Thresholds by State | Complete Guide | GoTruTax</title>
        <meta name="description" content="Complete guide to economic nexus thresholds for all 50 US states in 2025. Learn when you must register for sales tax permits, transaction count requirements, and compliance deadlines." />
        <meta name="keywords" content="economic nexus by state, nexus thresholds 2025, sales tax nexus requirements, state sales tax thresholds, when to register for sales tax, nexus threshold guide, remote seller nexus, nexus compliance by state" />
        <link rel="canonical" href="https://gotrutax.com/resources/state-nexus-thresholds" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": "2025 Economic Nexus Thresholds by State - Complete Guide",
            "description": "Comprehensive reference guide showing economic nexus thresholds for all US states including sales amounts, transaction counts, and compliance notes.",
            "author": {
              "@type": "Organization",
              "name": "GoTruTax"
            },
            "publisher": {
              "@type": "Organization",
              "name": "GoTruTax",
              "logo": {
                "@type": "ImageObject",
                "url": "https://gotrutax.com/logo.svg"
              }
            },
            "datePublished": "2025-11-29",
            "dateModified": "2025-11-29"
          })}
        </script>
      </Helmet>

      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ mb: 6 }}>
          <Typography variant="overline" color="primary" sx={{ fontWeight: 600 }}>
            State-by-State Reference
          </Typography>
          <Typography variant="h1" sx={{ fontSize: '2.5rem', fontWeight: 800, mb: 2, mt: 1 }}>
            2025 Economic Nexus Thresholds by State
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 3 }}>
            Complete reference guide for economic nexus thresholds across all US states. Updated for 2025 requirements.
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
            <Chip label="Economic Nexus" size="small" color="primary" />
            <Chip label="Sales Tax" size="small" />
            <Chip label="2025 Updated" size="small" color="success" />
          </Stack>
        </Box>

        {/* Quick Summary */}
        <Paper sx={{ p: 4, mb: 4, bgcolor: 'primary.50' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Quick Summary</Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Most Common Threshold</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>$100,000</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>States with $100k Threshold</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>38 States</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>No Sales Tax States</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>5 States</Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* States Table */}
        <Paper sx={{ p: 4, mb: 4 }}>
          <Typography variant="h2" sx={{ fontSize: '1.75rem', fontWeight: 700, mb: 3 }}>
            Economic Nexus Thresholds by State
          </Typography>
          
          <Box sx={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e0e0e0' }}>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>State</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Sales Threshold</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Transaction Count</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Notes</th>
                </tr>
              </thead>
              <tbody>
                {states.map((state, index) => (
                  <tr key={state.name} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '12px', fontWeight: 500 }}>{state.name}</td>
                    <td style={{ padding: '12px' }}>{state.threshold}</td>
                    <td style={{ padding: '12px' }}>{state.transactions}</td>
                    <td style={{ padding: '12px', fontSize: '0.875rem', color: '#666' }}>{state.notes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        </Paper>

        {/* No Sales Tax States */}
        <Paper sx={{ p: 4, mb: 4, bgcolor: 'success.50', border: '2px solid', borderColor: 'success.200' }}>
          <Typography variant="h2" sx={{ fontSize: '1.75rem', fontWeight: 700, mb: 2 }}>
            States Without Sales Tax
          </Typography>
          <Typography paragraph>
            Five states do not impose a general sales tax:
          </Typography>
          <Grid container spacing={2}>
            {['Alaska', 'Delaware', 'Montana', 'New Hampshire', 'Oregon'].map(state => (
              <Grid item xs={12} sm={6} md={4} key={state}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>{state}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      No state-level sales tax
                      {state === 'Alaska' && ' (local taxes may apply)'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>

        {/* Monitoring Tool CTA */}
        <Paper sx={{ p: 4, bgcolor: 'primary.main', color: 'white', textAlign: 'center' }}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
            Automate Your Nexus Monitoring
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, opacity: 0.9 }}>
            Stop manually tracking sales by state. GoTruTax automatically monitors your sales across all states and alerts you when approaching thresholds.
          </Typography>
          <MuiLink
            component={RouterLink}
            to="/tax/nexus-threshold"
            sx={{
              display: 'inline-block',
              bgcolor: 'white',
              color: 'primary.main',
              px: 4,
              py: 1.5,
              borderRadius: 2,
              fontWeight: 600,
              textDecoration: 'none',
              '&:hover': {
                bgcolor: 'grey.100'
              }
            }}
          >
            Try Nexus Threshold Checker
          </MuiLink>
        </Paper>

        {/* Related Resources */}
        <Box sx={{ mt: 6 }}>
          <Typography variant="h3" sx={{ fontSize: '1.5rem', fontWeight: 700, mb: 3 }}>
            Related Resources
          </Typography>
          <Stack spacing={2}>
            <MuiLink component={RouterLink} to="/blog/sales-tax-compliance-guide-2025" underline="hover">
              → Complete Sales Tax Compliance Guide for 2025
            </MuiLink>
            <MuiLink component={RouterLink} to="/resources/blogs-knowledge" underline="hover">
              → Tax Compliance Blog & Knowledge Base
            </MuiLink>
            <MuiLink component={RouterLink} to="/tax/calculator" underline="hover">
              → Free Sales Tax Calculator
            </MuiLink>
            <MuiLink component={RouterLink} to="/get-started" underline="hover">
              → Start Free Nexus Monitoring
            </MuiLink>
          </Stack>
        </Box>
      </Container>
    </>
  );
};

export default StateNexusGuide;
